// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import type {
  WizardStepId,
  StepFieldDefinition,
  WizardFieldError,
  StepValidationResult,
} from '../types/wizard';
import { toIsoDate } from '../utils/date';

interface UseStepValidationResult {
  fieldErrors: Record<string, string>;
  errorSummary: WizardFieldError[];
  hasErrors: boolean;
  validateStep: (
    stepId: WizardStepId,
    data: Record<string, unknown>,
    options?: { silent?: boolean },
  ) => StepValidationResult;
  validateAllSteps: (
    stepsData: Partial<Record<WizardStepId, Record<string, unknown>>>,
    activeSteps: readonly WizardStepId[],
  ) => {
    valid: boolean;
    firstErrorStep: WizardStepId | null;
    stepErrors: Partial<Record<WizardStepId, WizardFieldError[]>>;
  };
  clearErrors: () => void;
  clearFieldError: (field: string) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function isValidDate(value: string): boolean {
  const iso = toIsoDate(value);
  if (!DATE_REGEX.test(iso)) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime());
}

function validateField(
  field: StepFieldDefinition,
  value: unknown,
  allData: Record<string, unknown>,
): string | null {
  // Check conditional required
  if (field.requiredWhen) {
    const conditionValue = allData[field.requiredWhen.field];
    const conditionMet = conditionValue === field.requiredWhen.value;
    if (conditionMet && isEmptyValue(value)) {
      return field.type === 'file'
        ? 'Please upload the required document'
        : 'This field is required';
    }
  }

  // Check required
  if (field.required && isEmptyValue(value)) {
    return 'This field is required';
  }

  // Skip further validation if value is empty and not required
  if (isEmptyValue(value)) return null;

  const strValue = String(value);

  // Max length
  if (field.maxLength && strValue.length > field.maxLength) {
    return `Maximum ${field.maxLength} characters allowed`;
  }

  // Email validation
  if (field.type === 'email' && !EMAIL_REGEX.test(strValue)) {
    return 'Please enter a valid email address';
  }

  // Date validation
  if (field.type === 'date') {
    if (!isValidDate(strValue)) {
      return 'Please enter a valid date';
    }
    // Past date check (for DOB)
    if (field.pastDate) {
      const date = new Date(toIsoDate(strValue));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date >= today) {
        return 'Date of birth must be in the past';
      }
    }
  }

  // Currency non-negative
  if (field.type === 'currency') {
    const num = parseFloat(strValue);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }
    if (num < 0) {
      return 'Amount must be zero or greater';
    }
  }

  // Number validation
  if (field.type === 'number') {
    const num = parseFloat(strValue);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }
    if (field.min !== undefined && num < field.min) {
      return `Minimum value is ${field.min}`;
    }
    if (field.max !== undefined && num > field.max) {
      return `Maximum value is ${field.max}`;
    }
  }

  return null;
}

export function useStepValidation(
  fieldDefinitions: Partial<Record<WizardStepId, StepFieldDefinition[]>>,
): UseStepValidationResult {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorSummary, setErrorSummary] = useState<WizardFieldError[]>([]);
  const [hasErrors, setHasErrors] = useState(false);

  const validateStep = useCallback(
    (
      stepId: WizardStepId,
      data: Record<string, unknown>,
      options?: { silent?: boolean },
    ): StepValidationResult => {
      const definitions = fieldDefinitions[stepId];
      if (!definitions) return { valid: true, errors: [] };

      const errors: WizardFieldError[] = [];
      const newFieldErrors: Record<string, string> = {};

      for (const field of definitions) {
        const value = data[field.key];
        const error = validateField(field, value, data);
        if (error) {
          errors.push({ field: field.key, message: error });
          newFieldErrors[field.key] = error;
        }
      }

      if (!options?.silent) {
        setFieldErrors(newFieldErrors);
        setErrorSummary(errors);
        setHasErrors(errors.length > 0);
      }

      return { valid: errors.length === 0, errors };
    },
    [fieldDefinitions],
  );

  const validateAllSteps = useCallback(
    (
      stepsData: Partial<Record<WizardStepId, Record<string, unknown>>>,
      activeSteps: readonly WizardStepId[],
    ): {
      valid: boolean;
      firstErrorStep: WizardStepId | null;
      stepErrors: Partial<Record<WizardStepId, WizardFieldError[]>>;
    } => {
      const stepErrors: Partial<Record<WizardStepId, WizardFieldError[]>> = {};
      let firstErrorStep: WizardStepId | null = null;

      // Validate all active steps except Step 0 (loa-validation gate)
      for (let i = 1; i < activeSteps.length; i++) {
        const stepId = activeSteps[i];
        const definitions = fieldDefinitions[stepId];
        if (!definitions) continue;

        const data = stepsData[stepId] || {};
        const errors: WizardFieldError[] = [];

        for (const field of definitions) {
          const value = data[field.key];
          const error = validateField(field, value, data);
          if (error) {
            errors.push({ field: field.key, message: error });
          }
        }

        if (errors.length > 0) {
          stepErrors[stepId] = errors;
          if (!firstErrorStep) {
            firstErrorStep = stepId;
          }
        }
      }

      const valid = !firstErrorStep;
      return { valid, firstErrorStep, stepErrors };
    },
    [fieldDefinitions],
  );

  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setErrorSummary([]);
    setHasErrors(false);
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setErrorSummary((prev) => {
      const next = prev.filter((e) => e.field !== field);
      setHasErrors(next.length > 0);
      return next;
    });
  }, []);

  return {
    fieldErrors,
    errorSummary,
    hasErrors,
    validateStep,
    validateAllSteps,
    clearErrors,
    clearFieldError,
  };
}
